import { Module } from 'vuex'
import { SpokenPart, TranscriptPayloadV2 } from '../../transcription.api'
import countBy from 'lodash/countBy'
import uniqBy from 'lodash/uniqBy'
import map from 'lodash/map'
import find from 'lodash/find'
import reject from 'lodash/reject'
import { tagsColorPalette } from './tagsPicker'
import findIndexOfUsedWord from '../../utils/findIndexOfUsedWord'
import uuid from '../../utils/uuid'

//TODO: move interfaces to separate file
export interface Speaker {
  id: number
  name: string
  color: string
}

export interface SuggestedSpeaker extends Speaker {
  isTagged: boolean
  canBeDeleted: boolean
}

export interface Word {
  text: string
  start: number
}

export interface Part {
  id: string
  text: string
  start: number
  speaker?: Speaker
  words: Word[]
}

export interface CursorPosition {
  partId: string
  position: number
}

type RewindCallback = ({ start }: { start: number }) => void

//if true no errors will be displayed in case if clicked word was not found
const SILENT_MISSING_WORDS = false

interface State {
  //TODO: extract to separate module SpeakerSelectorPopupState
  speakerSelectorActivePart?: string
  speakerSelectorActiveTarget?: Record<string, unknown>

  parts: Part[]
  version: number
  speakers: Speaker[]
  speakerRefCount: Record<string, number> // speakerId -> number of parts that uses this speaker

  playerRewindCallback?: RewindCallback
  pendingCursorPosition?: CursorPosition
}

const editor: Module<State, Record<string, unknown>> = {
  namespaced: true,
  state: {
    speakers: [],
    speakerRefCount: {},
    version: 0,
    parts: [],
    speakerSelectorActiveTarget: undefined,
    speakerSelectorActivePart: undefined,
    playerRewindCallback: undefined,
    pendingCursorPosition: undefined
  },
  getters: {
    parts(state): Part[] {
      return state.parts
    },
    part: state => (partId): Part => {
      return find(state.parts, ['id', partId]) as Part
    },
    speaker: state => (speakerId): Speaker => {
      return find(state.speakers, ['id', speakerId]) as Speaker
    },
    activePart(state, getters): Part {
      return getters.part(state.speakerSelectorActivePart)
    },
    selectorShown(state): boolean {
      return Boolean(
        state.speakerSelectorActivePart && state.speakerSelectorActiveTarget
      )
    },
    selectorTarget(state) {
      return state.speakerSelectorActiveTarget
    },
    suggestedSpeakers(state, getters) {
      const part = getters.activePart

      return state.speakers
        .sort(({ id }) => (id === part?.speaker?.id ? -1 : 1)) //Current speaker first
        .map<SuggestedSpeaker>(speaker => ({
          ...speaker, //Add part related metadata
          isTagged: part?.speaker?.id === speaker.id, //is current speaker
          canBeDeleted: !state.speakerRefCount[speaker.id] //if speaker is referenced 0 times it can be safely deleted
        }))
    },
    pendingCursorPosition(state) {
      return state.pendingCursorPosition
    }
  },
  mutations: {
    SET_POPUP_TARGET(state, { partId, target }) {
      state.speakerSelectorActiveTarget = target
      state.speakerSelectorActivePart = partId
    },

    SET_PARTS(state, parts: Part[]) {
      state.parts = parts
    },

    SET_VERSION(state, version: number) {
      state.version = version
    },

    EXTRACT_SPEAKERS(state, parts) {
      state.speakerRefCount = countBy(parts, 'speaker.id')
      state.speakers = map(uniqBy(parts, 'speaker.id'), 'speaker')
    },

    REMOVE_SPEAKER(state, id) {
      state.speakers = reject(state.speakers, ['id', id])
      delete state.speakerRefCount[id]
    },

    ADD_SPEAKER(state, speaker: Speaker) {
      state.speakers.push(speaker)
      state.speakerRefCount[speaker.id] = 0
    },

    SET_SPEAKER(state, speaker?: Speaker) {
      //TODO: DRY it
      const activePart = find(state.parts, [
        'id',
        state.speakerSelectorActivePart
      ]) as Part

      //Correct speaker reference count (could be done by recount, but I want to save performance here too)
      const oldSpeakerId = activePart?.speaker?.id
      const newSpeakerId = speaker?.id
      oldSpeakerId && state.speakerRefCount[oldSpeakerId]--
      newSpeakerId && state.speakerRefCount[newSpeakerId]++

      activePart.speaker = speaker
    },

    UPDATE_PART_TEXT(state, { partId, text }) {
      const part = find(state.parts, ['id', partId])

      if (!part) {
        throw new Error(`Part not found. ID: ${partId}`)
      }

      part.text = text.trim()
    },

    SET_REWIND_CALLBACK(state, callback: RewindCallback) {
      state.playerRewindCallback = callback
    },

    INSERT_PART(state, { part, position }: { part: Part; position: number }) {
      state.parts.splice(position, 0, part)

      part.speaker && state.speakerRefCount[part.speaker.id]++
    },

    REMOVE_PART(state, position) {
      const speaker = state.parts[position]?.speaker
      speaker && state.speakerRefCount[speaker.id]--

      state.parts.splice(position, 1)
    },
    SET_PENDING_CURSOR_POSITION(state, position?: CursorPosition) {
      state.pendingCursorPosition = position
    }
  },
  actions: {
    init({ commit }, { parts, version }) {
      console.debug(`[Editor] Loading transcript. Version ${version}`)

      commit('SET_PARTS', parts)
      commit('SET_VERSION', version)
      commit('EXTRACT_SPEAKERS', parts)
    },
    async save({ state, getters }): Promise<TranscriptPayloadV2> {
      console.debug('[Editor] Saving.')

      return {
        version: state.version,
        parts: (getters.parts as Part[]).map<SpokenPart>(part => ({
          text: part.text,
          speaker: part?.speaker?.name ?? 'Unknown speaker' //TODO: do smth with this
        }))
      }
    },
    updatePart({ commit, state }, { partId, text }) {
      if (!state.version) {
        throw new Error('Editor is not initialized')
      }

      console.debug(`[Editor] Saving part ${partId}`)

      commit('UPDATE_PART_TEXT', { partId, text })
    },
    showSpeakerSelector({ commit }, { partId, target }) {
      commit('SET_POPUP_TARGET', { partId, target })
    },
    hideSpeakerSelector({ commit }) {
      commit('SET_POPUP_TARGET', { partId: undefined, target: undefined })
    },
    selectSpeaker({ commit, getters }, id) {
      commit('SET_SPEAKER', getters.speaker(id))
    },
    unselectSpeaker({ commit }) {
      commit('SET_SPEAKER', undefined)
    },
    createAndSelectSpeaker({ commit, dispatch, state }, name) {
      const id = state.speakers.length + 5 //TODO: rewrite to uuid, ask Ihor why '5'
      const color = tagsColorPalette[id % tagsColorPalette.length] //Magic

      commit('ADD_SPEAKER', { id, name, color })
      dispatch('selectSpeaker', id)
    },

    deleteSpeaker({ commit, state }, id) {
      if (state.speakerRefCount[id] > 0) {
        throw new Error("Can't delete speaker that is in use")
      }

      commit('REMOVE_SPEAKER', id)
    },

    registerAudioRewindCallback({ commit }, callback: RewindCallback) {
      commit('SET_REWIND_CALLBACK', callback)
    },

    rewindToWord({ state, getters }, { partId, word, offset }) {
      if (!state.playerRewindCallback) {
        throw new Error('Rewind callback is not set')
      }

      //TODO: think about getting text directly from field instead of part, as it might be in another state

      const part = getters.part(partId)
      //Find all word instances with searchable word
      const foundWords = part.words.filter(
        ({ text }) => text.toLowerCase() === word
      )

      //If we have same word multiple times, here we will find which one was clicked
      const index = findIndexOfUsedWord(part.text, word, offset)
      const wordInstance = foundWords[index]

      if (wordInstance) {
        state.playerRewindCallback(wordInstance)
        return
      }

      if (SILENT_MISSING_WORDS) {
        return
      }

      console.warn(`[Editor] Word [${word}] not found`)
      console.debug(`[Editor] Offset ${offset}, index ${index}, part ${partId}`)
      console.debug('[Editor] Found words', foundWords)
    },

    splitPart({ commit, getters, state, dispatch }, { partId, offset }) {
      const oldPart = getters.part(partId)
      const partIndex = state.parts.indexOf(oldPart)

      const oldPartText = oldPart.text.substring(0, offset).trim()
      const newPartText = oldPart.text.substring(offset).trim()

      const newPart: Part = {
        id: uuid(),
        start: 0,
        text: newPartText,
        speaker: oldPart.speaker,
        words: []
      }

      commit('UPDATE_PART_TEXT', { partId, text: oldPartText }) //TODO: words array
      commit('INSERT_PART', {
        part: newPart,
        position: partIndex + 1 //After old
      })
      dispatch('moveCursorTo', {
        partId: newPart.id,
        position: 0
      })
    },

    joinPreviousPart({ commit, getters, state, dispatch }, { partId }) {
      const partToDelete = getters.part(partId)
      const partToDeleteIndex = state.parts.indexOf(partToDelete)

      if (partToDeleteIndex === 0) {
        return //Can't join first part
      }

      const partToJoin = state.parts[partToDeleteIndex - 1]
      const cursorPosition = partToJoin.text.length
      const combinedText = partToJoin.text + ' ' + partToDelete.text

      commit('UPDATE_PART_TEXT', { partId: partToJoin.id, text: combinedText }) //TODO: words array
      commit('REMOVE_PART', partToDeleteIndex)
      dispatch('moveCursorTo', {
        partId: partToJoin.id,
        position: cursorPosition
      })
    },

    joinNextPart({ commit, getters, state, dispatch }, { partId }) {
      const partToJoin = getters.part(partId)
      const partToJoinIndex = state.parts.indexOf(partToJoin)

      if (partToJoinIndex === state.parts.length - 1) {
        return //Can't join last part
      }

      const cursorPosition = partToJoin.text.length
      const partToDeleteIndex = partToJoinIndex + 1
      const partToDelete = state.parts[partToDeleteIndex]
      const combinedText = partToJoin.text + ' ' + partToDelete.text

      commit('UPDATE_PART_TEXT', { partId: partToJoin.id, text: combinedText }) //TODO: words array
      commit('REMOVE_PART', partToDeleteIndex)
      dispatch('moveCursorTo', {
        partId: partToJoin.id,
        position: cursorPosition
      })
    },

    moveCursorTo({ commit }, { partId, position }: CursorPosition) {
      commit('SET_PENDING_CURSOR_POSITION', { partId, position })
    },

    acknowledgeCursorMove({ commit }) {
      commit('SET_PENDING_CURSOR_POSITION', undefined)
    }
  }
}

export default editor
